// Globálny dirty guard (ústava 53.1 / 53.6 / #5) — modulový singleton, nezávislý na routeri.
// Moduly hlásia neuložený stav: setDirty(key, bool, saveFn?). saveFn = ako uložiť (pre tlačidlo „Uložiť").
// Navigačné body (prepnutie zákazky/tabu/modulu) volajú `await confirmLeave()` pred odchodom.
// 3-tlačidlový dialóg (Uložiť / Zahodiť / Zrušiť) — registruje ho <UnsavedGuardDialog/> v Layout.
// beforeunload chráni pri refreshi / zatvorení tabu prehliadača.
// react-router v6.22 BrowserRouter nepodporuje useBlocker (treba data-router), preto singleton.

const _dirty = new Map()   // key -> saveFn | null
const _discard = new Map() // key -> discardFn | null (straz_v1: po „Zahodiť“ vráti formulár na uložený stav)
let _installed = false
let _showDialog = null      // () => Promise<'save'|'discard'|'cancel'>  (registruje React dialóg)

function _install() {
  if (_installed || typeof window === 'undefined') return
  _installed = true
  window.addEventListener('beforeunload', (e) => {
    if (_dirty.size > 0) { e.preventDefault(); e.returnValue = '' }
  })
}

// Modul nahlási / zruší svoj neuložený stav. saveFn (voliteľné) = async funkcia ktorá uloží.
export function setDirty(key, isDirty, saveFn, discardFn) {
  _install()
  if (isDirty) { _dirty.set(key, typeof saveFn === 'function' ? saveFn : null); _discard.set(key, typeof discardFn === 'function' ? discardFn : null) }
  else { _dirty.delete(key); _discard.delete(key) }
}

// straz_v1 (28. 9. 2026, Mirko: „mal som byť upozornený pred opustením“): „Uložiť“ v dialógu smie byť len vtedy,
// keď každý rozpísaný formulár vie uložiť sám; inak by sa zmeny ticho zahodili.
export function canSaveAll() { return [..._dirty.values()].every(Boolean) }
export function dirtyKeys() { return [..._dirty.keys()] }
if (typeof window !== 'undefined') window.__strazMatrix = { isAnyDirty, dirtyKeys }   // straz_v1: pohľad pre skúšky a ladenie (len čítanie)
function _discardAll() { for (const fn of [..._discard.values()]) { try { if (fn) fn() } catch (_) {} } _dirty.clear(); _discard.clear() }

export function isAnyDirty() { return _dirty.size > 0 }

// straz_v1: obal pre vlastné riadky zoznamov (tabuľky, karty dopytov), ktoré nepoužívajú ListRow — pred prepnutím sa spýta
export const strazKlik = (fn) => async (e) => { if (isAnyDirty() && !(await confirmLeave())) return; if (fn) fn(e) }

// React dialóg sa zaregistruje sem (mount v Layout).
export function registerUnsavedDialog(fn) { _showDialog = fn }

// Vráti Promise<boolean> — true ak SMIE odísť (uložené/zahodené), false ak zostáva.
export async function confirmLeave() {
  if (_dirty.size === 0) return true
  // fallback ak nie je React dialóg (2 tlačidlá)
  if (!_showDialog) {
    const ok = window.confirm('Máš NEULOŽENÉ zmeny. Odísť bez uloženia? OK = zahodiť · Zrušiť = zostať')
    if (ok) _discardAll()
    return ok
  }
  const choice = await _showDialog({ canSave: canSaveAll() })
  if (choice === 'cancel') return false
  if (choice === 'save') {
    if (!canSaveAll()) return false   // straz_v1: formulár bez vlastného ukladania — zostaň, ulož tlačidlom na obrazovke
    const fns = [..._dirty.values()].filter(Boolean)
    if (fns.length) {
      try { for (const fn of fns) { if ((await fn()) === false) return false } }   // p95: false = uloženie zlyhalo → zostaň
      catch (_) { return false }   // uloženie zlyhalo → zostaň, dirty ostáva
    }
    _dirty.clear(); _discard.clear(); return true
  }
  // 'discard' — formuláre sa vrátia na uložený stav (inak by po odchode na inú záložku ostali rozpísané bez stráže)
  _discardAll(); return true
}

// ── p95 DOKLAD = OBRAZOVKA (ústava 67.15; Mirko 21. 9. 2026: „Odoslal som objednávku v domnení, že posledné vymazané
// veci tam nebudú, no neurobil som Uložiť — objednávka tak odišla, ako nemala. Toto sa nemôže nikdy viac stať!").
// Pred každou akciou, ktorá robí doklad (odoslanie e-mailom, PDF, tlač, náhľad), sa neuložené zmeny ULOŽIA bez pýtania.
// Keď sa uložiť nedajú (chyba, povinné pole) alebo modul nevie uložiť sám, akcia sa ZASTAVÍ. „Zahodiť" tu nie je voľba —
// doklad nesmie odísť iný, než aký je na obrazovke. saveFn vracia true/false (false = neuložené).
export async function ulozPredDokumentom() {
  for (const [key, fn] of [..._dirty.entries()]) {
    if (!fn) return false
    let r
    try { r = await fn() } catch (_) { return false }
    if (r === false) return false
    _dirty.delete(key)
  }
  return true
}

// To isté s hláškou pre používateľa; true = smie sa pokračovať.
export async function pripravDokument(toast) {
  if (await ulozPredDokumentom()) return true
  const t = 'Na obrazovke sú zmeny, ktoré sa nepodarilo uložiť — doklad sa NEODOSLAL ani nevytlačil. Skontroluj povinné polia, ulož a skús znova.'
  if (toast && toast.error) toast.error(t); else window.alert(t)
  return false
}

// PDF alebo tlač v novom okne až po uložení. Okno sa otvorí hneď (inak ho prehliadač po čakaní zablokuje),
// adresa sa doplní po uložení; keď sa uložiť nedá, okno sa zavrie.
export async function otvorDokument(url, toast) {
  const w = window.open('', '_blank')
  if (!(await pripravDokument(toast))) { try { if (w) w.close() } catch (_) {} return false }
  const u = typeof url === 'function' ? await url() : url
  if (w) w.location.href = u; else window.open(u, '_blank')
  return true
}
